/**
 * Retry timing shared by the long-lived connections the admin app keeps
 * open (the Twilio Device registration, token refreshes).
 *
 * A delay table rather than a formula: the steps are chosen by hand so
 * a short blip recovers in seconds while a dead backend is polled only
 * every few minutes, and a test can name every step.
 */

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** The delay before retry number `attempt` (0-based), clamped to the table. */
export const delayForAttempt = (delays: readonly number[], attempt: number): number => {
    const index = Math.min(Math.max(attempt, 0), delays.length - 1);
    // An empty table has no step to wait on.
    return delays[index] ?? 0;
};

/**
 * Run `fn`, retrying once per entry in `delays` after a failure. The
 * last error is rethrown once the table is exhausted.
 */
export async function retryAsync<T>(
    fn: () => Promise<T>,
    delays: readonly number[],
    wait: (ms: number) => Promise<void> = sleep,
): Promise<T> {
    let attempt = 0;
    for (;;) {
        try {
            return await fn();
        } catch (err) {
            if (attempt >= delays.length) throw err;
            await wait(delayForAttempt(delays, attempt));
            attempt += 1;
        }
    }
}
